import React, { Fragment, useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import config from "../../config";

const useStyles = makeStyles({
  headerStyle: {
    fontStyle: "normal",
    color: "#fff",
    fontWeight: "900 !important",
    marginBottom: "15px !important",
  },
  goldButton: {
    backgroundColor: "rgb(255, 192, 1) !important",
    color: "#000 !important",
    fontWeight: "700 !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "#e6ac00 !important",
    },
  },
  outlineButton: {
    borderColor: "rgb(255, 192, 1) !important",
    color: "rgb(255, 192, 1) !important",
    textTransform: "none !important",
    "&:hover": {
      backgroundColor: "rgba(255, 192, 1, 0.08) !important",
    },
  },
  darkInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#111",
      color: "#fff",
      "& fieldset": { borderColor: "#333" },
      "&:hover fieldset": { borderColor: "rgb(255, 192, 1)" },
      "&.Mui-focused fieldset": { borderColor: "rgb(255, 192, 1)" },
    },
    "& .MuiInputLabel-root": { color: "#ccc" },
  },
});

const EmailVerification = ({ type }) => {
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [loadingResend, setLoadingResend] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  useEffect(() => {
    const stored = sessionStorage.getItem("pendingVerificationEmail");
    if (stored) {
      setEmail(stored);
      setNewEmail(stored);
    }
  }, []);

  const firstPara =
    "We have sent an e-mail to you for verification. It's necessary that we verify a working email address because this is how you will receive responses back from sellers.";
  const secondPara =
    "If you do not receive it within several minutes, it may have been blocked by your spam filter (check spam and promotional folders) or you may have typed in the wrong email address.";

  const handleResend = async () => {
    if (!email) {
      setFeedback({ type: "error", message: "No email on record. Please sign up again." });
      return;
    }
    setLoadingResend(true);
    setFeedback({ type: "", message: "" });
    try {
      await axios.post(
        `${config.SERVER_URL}/api/users/confirmation`,
        { user: { email } },
        { headers: { "Content-Type": "application/json" } }
      );
      setFeedback({
        type: "success",
        message: "Verification email resent. Please check your inbox.",
      });
    } catch ({ response }) {
      const msg =
        (response && response.data && response.data.errors && JSON.stringify(response.data.errors)) ||
        "Failed to resend. Please try again.";
      setFeedback({ type: "error", message: msg });
    }
    setLoadingResend(false);
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!email) {
      setFeedback({ type: "error", message: "No email on record. Please sign up again." });
      return;
    }
    if (newEmail === email) {
      setFeedback({ type: "error", message: "New email is the same as the current one." });
      return;
    }
    setLoadingUpdate(true);
    setFeedback({ type: "", message: "" });
    try {
      const res = await axios.post(
        `${config.SERVER_URL}/api/users/update_unconfirmed_email`,
        { old_email: email, new_email: newEmail },
        { headers: { "Content-Type": "application/json" } }
      );
      const updatedEmail = (res.data && res.data.email) || newEmail;
      setEmail(updatedEmail);
      sessionStorage.setItem("pendingVerificationEmail", updatedEmail);
      setEditMode(false);
      setFeedback({
        type: "success",
        message: "Email updated. Verification instructions sent to the new address.",
      });
    } catch ({ response }) {
      const msg =
        (response && response.data && response.data.errors && JSON.stringify(response.data.errors)) ||
        "Failed to update email. Please try again.";
      setFeedback({ type: "error", message: msg });
    }
    setLoadingUpdate(false);
  };

  return (
    <Stack
      style={{ backgroundColor: "#111", minHeight: "80vh" }}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Card
        sx={{
          padding: "30px",
          margin: "60px",
          width: { xs: "90%", md: "40%" },
          backgroundColor: "#1a1a1a",
          border: "1px solid #333",
        }}
      >
        <CardContent>
          <Typography variant="h4" align="center" className={classes.headerStyle}>
            {type === "signup" ? "Verify Your Email" : "Password Reset"}
          </Typography>

          <Typography sx={{ color: "#ccc", mb: 2 }}>
            {type === "signup" ? (
              <Fragment>
                {firstPara}
                <br />
                <br />
                {secondPara}
              </Fragment>
            ) : (
              "We have sent you an e-mail. Please contact us if you do not receive it within a few minutes."
            )}
          </Typography>

          {type === "signup" && email && (
            <Box
              sx={{
                backgroundColor: "#111",
                border: "1px solid #333",
                borderRadius: "8px",
                padding: "15px",
                mt: 2,
                mb: 2,
              }}
            >
              <Typography sx={{ color: "#ccc", fontSize: "14px" }}>
                Verification email sent to:
              </Typography>
              <Typography sx={{ color: "rgb(255, 192, 1)", fontWeight: 700, wordBreak: "break-all" }}>
                {email}
              </Typography>
            </Box>
          )}

          {feedback.message && (
            <Alert
              severity={feedback.type || "info"}
              sx={{ mb: 2 }}
              onClose={() => setFeedback({ type: "", message: "" })}
            >
              {feedback.message}
            </Alert>
          )}

          {type === "signup" && email && !editMode && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                className={classes.goldButton}
                onClick={handleResend}
                disabled={loadingResend}
              >
                {loadingResend ? <CircularProgress size={22} sx={{ color: "#000" }} /> : "Resend Email"}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                className={classes.outlineButton}
                onClick={() => setEditMode(true)}
              >
                Edit Email
              </Button>
            </Stack>
          )}

          {type === "signup" && editMode && (
            <Box component="form" onSubmit={handleUpdateEmail} sx={{ mt: 2 }}>
              <Typography sx={{ color: "#ccc", fontSize: "14px", mb: 1 }}>
                Enter the correct email address:
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                className={classes.darkInput}
                sx={{ mb: 2 }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className={classes.goldButton}
                  disabled={loadingUpdate}
                >
                  {loadingUpdate ? (
                    <CircularProgress size={22} sx={{ color: "#000" }} />
                  ) : (
                    "Update & Resend"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  className={classes.outlineButton}
                  onClick={() => {
                    setEditMode(false);
                    setNewEmail(email);
                  }}
                  disabled={loadingUpdate}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default EmailVerification;
